import React from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Avatar,
  Divider,
  useColorModeValue,
  Icon,
  Button,
  HStack,
  CardFooter
} from '@chakra-ui/react';
import { FaQuoteLeft, FaQuoteRight, FaShare } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Testimonies = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const purpleColor = useColorModeValue('purple.600', 'purple.300');
  const orangeColor = useColorModeValue('orange.500', 'orange.300');
  
  const testimonies = [
    {
      name: "Sarah J.",
      role: "Youth Member",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg",
      quote: "Alpha Youth changed my life! I came in feeling lost but found purpose and a family that supports me in my faith journey.",
      date: "March 2023"
    },
    {
      name: "Michael T.",
      role: "Volunteer Leader",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
      quote: "Seeing young people encounter God for the first time is the most rewarding experience. This ministry is truly making an impact.",
      date: "January 2023"
    },
    {
      name: "Emily R.",
      role: "Parent",
      avatar: "https://randomuser.me/api/portraits/women/68.jpg",
      quote: "My daughter was transformed through the summer camp program. She's more confident in her faith and has found great Christian friends.",
      date: "August 2022"
    },
    {
      name: "David K.",
      role: "Youth Member",
      avatar: "https://randomuser.me/api/portraits/men/75.jpg",
      quote: "The Bible studies helped me understand Scripture in a way that relates to my daily life as a teenager. So grateful for this community!",
      date: "November 2022"
    },
    {
      name: "Pastor Mark",
      role: "Local Church Partner",
      avatar: "https://randomuser.me/api/portraits/men/22.jpg",
      quote: "Alpha Youth complements our church youth group perfectly. Their events bring energy and their leaders have genuine hearts for ministry.",
      date: "June 2023"
    },
    {
      name: "Jessica L.",
      role: "Former Member",
      avatar: "https://randomuser.me/api/portraits/women/33.jpg",
      quote: "Even after moving away for university, the lessons I learned at Alpha Youth continue to guide me. The foundation they built lasts!",
      date: "September 2021"
    }
  ];

  const shareTestimony = (text) => {
    if (navigator.share) {
      navigator.share({
        title: 'Alpha Youth Testimony',
        text: text,
        url: window.location.href
      }).catch(err => console.log('Error sharing:', err));
    } else {
      // Fallback for browsers that don't support Web Share API
      alert('Share this testimony: ' + text);
    }
  };

  return (
    <Box minH="100vh" mt={10} bg={bgColor} py={10} px={{ base: 4, md: 8 }}>
      <VStack spacing={10} maxW="1200px" mx="auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <VStack textAlign="center" spacing={4}>
            <Heading
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="extrabold"
              bgGradient={`linear(to-r, ${purpleColor}, ${orangeColor})`}
              bgClip="text"
            >
              Changed Lives
            </Heading>
            <Text fontSize="xl" color={textColor} maxW="800px">
              Hear from those who have experienced God's transformation through Alpha Youth UK
            </Text>
          </VStack>
        </motion.div>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} w="full">
          {testimonies.map((testimony, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <Card bg={cardBg} boxShadow="lg" borderRadius="xl" h="100%">
                <CardHeader>
                  <HStack spacing={4}>
                    <Avatar name={testimony.name} src={testimony.avatar} size="lg" />
                    <Box>
                      <Heading size="md">{testimony.name}</Heading>
                      <Text color={orangeColor}>{testimony.role}</Text>
                      <Text fontSize="sm" color="gray.500">{testimony.date}</Text>
                    </Box>
                  </HStack>
                </CardHeader>
                <Divider borderColor={purpleColor} opacity="0.5" />
                <CardBody position="relative">
                  <Icon
                    as={FaQuoteLeft}
                    color={purpleColor}
                    opacity="0.3"
                    position="absolute"
                    top={4}
                    left={4}
                    boxSize={6}
                  />
                  <Text color={textColor} px={6} py={2} fontStyle="italic">
                    {testimony.quote}
                  </Text>
                  <Icon
                    as={FaQuoteRight}
                    color={purpleColor}
                    opacity="0.3"
                    position="absolute"
                    bottom={4}
                    right={4}
                    boxSize={6}
                  />
                </CardBody>
                <CardFooter justify="flex-end">
                  <Button
                    leftIcon={<FaShare />}
                    variant="ghost"
                    colorScheme="purple"
                    onClick={() => shareTestimony(`${testimony.name}: ${testimony.quote}`)}
                  >
                    Share
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </SimpleGrid>

        <Box
          bgGradient={`linear(to-r, ${purpleColor}, ${orangeColor})`}
          p={8}
          borderRadius="xl"
          w="full"
          color="white"
          textAlign="center"
          boxShadow="xl"
        >
          <VStack spacing={4}>
            <Heading size="lg">Share Your Story</Heading>
            <Text fontSize="lg">
              Has Alpha Youth impacted your life? We'd love to hear your testimony!
            </Text>
            <Button
              as="a"
              href="/contact"
              colorScheme="whiteAlpha"
              rightIcon={<FaShare />}
              size="lg"
            >
              Submit Your Testimony
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default Testimonies;